import { FC, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCodeVerification, useLoginForm } from "../hooks";
import { Box, Button, Modal, ModalBody, ModalContent, ModalTrigger, OtpInput, Spinner, Tag, Typography } from "@orion/ui";
import { Close, User, UserAccess } from "@orion/icons";

export const CodeVerification: FC<{}> = () => {
  const { t } = useTranslation();
  const {
    OTP,
    setOTP,
    loading,
    submit,
    operationCode,
    token_serialNumber,
    token_userSeed,
    cancelLoginProcedure,
  } = useCodeVerification();

  const [openModal, setOpenModal] = useState(false);

  const isBtnDisabled = useMemo(() => {
    return loading || OTP.length < 6;
  }, [OTP, loading]);

  return (
    <Box
      flex
      column
      my9
      data-landmark="1"
      role="form"
      style={{
        width: "576px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "40px",
        position: "relative",
        zIndex: 1,
      }}
    >
      {loading ? (
        <Spinner size={"16"} />
      ) : (
        <>
          <Box
            flex
            gap15
            column
            style={{
              width: "576px",
              height: "auto",
            }}
          >
            <Box flex column itemsEnd>
              <Typography  >
                {t("login.codeVerification.operationCode")}{operationCode}
              </Typography>

            </Box>
            <Box flex column gap9>
              <Typography text2xl fontBold>
                {t("login.codeVerification.verify")}
              </Typography>
              <Typography textLg>
                <Trans i18nKey="login.codeVerification.generate" components={{ b: <b style={{ fontWeight: "bold" }} /> }} />
              </Typography>
              <Typography textNeutral70>
                {t("login.codeVerification.key")}
                <Typography asSpan textNeutral70 fontBold>
                  {token_serialNumber}
                </Typography>
              </Typography>
              <Tag bgNeutral20 p10 wFull>
                <Box flex gap10 itemsCenter>
                  <UserAccess style={{ width: 24, height: 24 }} />
                  <Box>
                    <Typography textLg>
                      {t("login.codeVerification.insertKey")}
                        <Typography asSpan textLg fontBold>
                          {token_userSeed}
                        </Typography>
                    </Typography>

                  </Box>
                    <Modal  open={openModal} onOpenChange={(open) => setOpenModal(true)}>
                    <ModalTrigger asChild>
                      <Button none outlinePrimary aria-label={t("login.codeVerification.moreInfo")}>
                        <Typography>{t("login.codeVerification.infoModal.moreInfo")}</Typography>
                      </Button>
                    </ModalTrigger>
                    <ModalContent>
                      <ModalBody >
                        <Box flex column gap10>
                          <Typography as="div" textLg fontBold>
                            {t("login.codeVerification.infoModal.title")}
                            </Typography>
                          <Typography as="div" style={{ whiteSpace: "pre-line" }}>
                            {t("login.codeVerification.infoModal.description")}
                          </Typography>
                          <Typography as="div">
                            {t("login.codeVerification.infoModal.bankValue")}
                          </Typography>
                          <Typography as="div">
                            {t("login.codeVerification.infoModal.otpValidity")}
                          </Typography>

                        </Box>
                        <Box flex itemsCenter justifyCenter mt5>
                          <Button outlinePrimary onClick={() => setOpenModal(false)}>{t("login.codeVerification.infoModal.close")}</Button>
                        </Box>
                      </ModalBody>
                    </ModalContent>
                  </Modal>
                </Box>
              </Tag>

            </Box>
          </Box>
          <Box style={{ width: "100%" }}>
            <form onSubmit={(e) => submit(e)}>
              <Box
                flex
                column
                style={{
                  width: "100%",
                  gap: "24px",
                  padding: "0 8px",
                }}
              >
                <Box flex justifyCenter>
                  <OtpInput
                    aria-label={t("accessibility.codeVerification.otp")}
                    aria-required="true"
                    value={OTP}
                    onChange={(input) => setOTP(input.toString())}
                  />
                  <Button
                    p0
                    variant="ghostPrimary"
                    onClick={() => setOTP("")}
                    type="button"
                    aria-label={t("accessibility.closeErrorMessage")}
                  >
                    <Close style={{ width: 24, height: 24 }} />
                  </Button>
                </Box>
                <Box justifyItems="center">
                  <Button type="submit" isDisabled={isBtnDisabled} minW52>
                    {t("login.confirm")}
                  </Button>
                </Box>
              </Box>
            </form>
            <Box
              flex
              alignItems="center"
              justifyCenter
              gap5
              style={{
                marginTop: "24px",
                padding: "0 8px",
              }}
            >
              <Typography textAlign="center" textNeutral70>
                {t("login.authFailure.cancelProcedure")}
              </Typography>
              <Button
                variant={"ghostPrimary"}
                p0
                onClick={() => cancelLoginProcedure()}
              >
                <Typography
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {t("login.cancel")}
                </Typography>
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CodeVerification;
